import {
  useGetOneEstimationMethod,
  useGetOneEstimationMethodRun,
} from "#hooks/adapters/useEstimationMethods";
import { useGetOneNetwork } from "#hooks/adapters/useNetworks";
import { useGetOneUser } from "#hooks/adapters/useUsers";
import { useParams } from "#routers/hooks";
import { NetworkDetailPageParameters } from "#routes/organization/networks/[networkId]/detail";
import type { CalculationsViewFilterAreaType } from "#src/batteries-included-components/FilterAreas/CalculationsFilterAreas";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { DEFAULT_DATE_RANGES } from "#src/hooks/useDateRange";
import { useStickyState } from "#src/hooks/useStickyState";
import { getMeasurementTypeFromNetworkCalculatorEstimationMethod } from "#src/view-models/asset-groups";
import { KeyValuePanel, StorageKeys } from "@validereinc/common-components";
import { AssetType } from "@validereinc/domain";
import {
  datetimeFormatter,
  getYearMonthFromDateRange,
  toTitleCaseString,
  yearMonthName,
} from "@validereinc/utilities";
import React from "react";
export const NetworkCalculationSummaryPanel = ({
  viewConfigStorageKey,
}: Pick<StorageKeys, "viewConfigStorageKey">) => {
  const { getUnitName, getPrecisionByType } = useMeasurementTypes();
  const { networkId } = useParams<NetworkDetailPageParameters>();
  const { data: assetGroup } = useGetOneNetwork(networkId);
  const [viewFilters] = useStickyState<Partial<CalculationsViewFilterAreaType>>(
    {},
    viewConfigStorageKey
  );
  const yearMonth = getYearMonthFromDateRange(
    viewFilters?.date_range ?? DEFAULT_DATE_RANGES.currentWholeMonth
  );

  const { data: run } = useGetOneEstimationMethodRun({
    id: assetGroup?.default_estimation_method_id ?? "",
    assetType: AssetType.ASSET_GROUP,
    yearMonth: yearMonth ?? "",
  });
  const { data: defaultEstimationMethod } = useGetOneEstimationMethod({
    id: assetGroup?.default_estimation_method_id ?? "",
    entityType: AssetType.ASSET_GROUP,
  });
  const { data: savedByUser } = useGetOneUser({ id: run?.updated_by });

  const measurementType = defaultEstimationMethod
    ? getMeasurementTypeFromNetworkCalculatorEstimationMethod(
        defaultEstimationMethod.data
      )
    : "";
  const measurementTypeHeader = toTitleCaseString(measurementType);

  const getDisplayValueForOutputType = (type: string) => {
    const value = run?.output?.outputs.find(
      ({ measurement_type }) => measurement_type === type
    );

    if (!value) {
      return "-";
    }

    const numericValue =
      typeof value.measurement_value === "number"
        ? value.measurement_value
        : Number(value.measurement_value);
    const precision = getPrecisionByType(value.measurement_type);
    return `${numericValue.toLocaleString(
      "en-US",
      precision
        ? {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
          }
        : {}
    )} ${
      value?.measurement_unit ? getUnitName(value?.measurement_unit) : null
    }`;
  };

  return (
    <KeyValuePanel
      panelProps={{ title: "Calculation Summary" }}
      data={[
        {
          title: `Total Upstream ${measurementTypeHeader}`,
          value: getDisplayValueForOutputType(`upstream_${measurementType}`),
        },
        {
          title: `Non-Adjustable Downstream ${measurementTypeHeader}`,
          value: getDisplayValueForOutputType(
            `downstream_${measurementType}_non_adjustable`
          ),
        },
        {
          title: `Adjustable Downstream ${measurementTypeHeader}`,
          value: getDisplayValueForOutputType(
            `downstream_${measurementType}_adjustable`
          ),
        },
        {
          title: `Adjusted Downstream ${measurementTypeHeader}`,
          value: getDisplayValueForOutputType(
            `downstream_${measurementType}_adjusted`
          ),
        },
        {
          title: "Adjustment Factor",
          value: getDisplayValueForOutputType(`adjustment_factor`),
        },
        {
          title: "Last Saved At",
          value: run?.updated_at
            ? datetimeFormatter(new Date(run.updated_at))
            : "-",
        },
        {
          title: "Saved By",
          value: savedByUser?.name,
        },
        {
          title: "Period",
          value: yearMonthName(yearMonth ?? ""),
        },
      ]}
    />
  );
};
